import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Caption1, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { isComplianceTracked, personDisplayName, type Person } from '@vigeo/shared';
import { lifecyclePresenters } from '@/features/candidates';
import { ReadinessBadge } from '@/features/readiness';
import { candidateStepRail, recordLinkRail, type CandidateStepSpec } from '@/features/people';
import type { ReadinessState } from '@vigeo/shared';

/**
 * The record chrome frames 06b `295:801` and `297:932` draw around a candidate step.
 *
 * Both frames put the same three things above the section: the record header with the person's
 * name and their two badges, a "Candidate steps" rail, and then the step itself as a single
 * bordered card. The rail and the card are SEPARATE cards with a gap between them and different
 * heights, which is why this is not `SectionWorkspace` — that renders one card split by a rule,
 * and stretches the rail to the content's height.
 *
 * The breadcrumb above it is the shell's (`AppLayout`), not this component's.
 */

/** The seven steps the frames name, in their order. */
export interface CandidateStep {
  readonly label: string;
  /**
   * Where the step goes, or null when the app has no screen for it yet.
   *
   * Three of the frame's seven have no destination in this build — see `candidateSteps`. They are
   * still drawn, because the frame draws them and a rail that skipped them would misstate how
   * many steps there are.
   */
  readonly href: string | null;
}

/** `?section=` on the person record, which is how that page selects a section. */
const section = (personId: string, id: string) => `/app/people/${encodeURIComponent(personId)}?section=${id}`;

/**
 * The rail, from the ONE definition of it (`@/features/people`, #462 §5).
 *
 * This used to be a second copy of the seven steps, and it had drifted: it drew **Orientation and
 * start** unlinked, with a comment saying step 7 had no screen, which stopped being true when #346
 * gave it one. The person record had moved on and these routes had not — two rails for the same
 * person, two clicks apart, which is what #332 was about.
 *
 * A step with no screen is still DRAWN, unlinked, because the frames draw seven and a rail that
 * skipped one would misstate how many there are.
 */
/**
 * `rail` is a parameter, defaulting to the one real rail.
 *
 * NOT for production callers — nothing passes it. It exists because every step is built as of
 * #487, so the unbuilt branch below is no longer reachable from real data, and a mechanism nothing
 * exercises is a mechanism that quietly stops working. `tests/offer-routes-rail.test.ts` hands it a
 * synthetic rail with an unbuilt step and asserts it is still drawn unlinked, which is what the
 * frames require of a step that has no screen yet.
 */
export function candidateSteps(personId: string, rail: readonly CandidateStepSpec[] = candidateStepRail): readonly CandidateStep[] {
  return rail.map((step) => ({
    label: step.label,
    href: step.built ? section(personId, step.id) : null,
  }));
}

/** The three links under the rail's divider. All three are sections of the record. */
function recordLinks(personId: string) {
  return recordLinkRail.map((link) => ({ label: link.label, href: section(personId, link.id) }));
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
  header: {
    display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM, flexWrap: 'wrap', minWidth: 0,
  },
  title: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  nameRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  name: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold, overflowWrap: 'anywhere' },
  roleRow: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  body: {
    display: 'grid', gridTemplateColumns: '13.75rem minmax(0, 1fr)', gap: tokens.spacingHorizontalL,
    alignItems: 'start', minWidth: 0,
    // The rail is a sibling card, not a column of the content card, so below tablet it simply
    // stacks above it rather than needing a select.
    [shellMedia.belowTablet]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  rail: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    padding: tokens.spacingHorizontalM, minWidth: 0,
  },
  railLabel: { paddingInline: tokens.spacingHorizontalXS, paddingBlockEnd: tokens.spacingVerticalXS },
  step: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalS, borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2, textDecorationLine: 'none', minWidth: 0, overflowWrap: 'anywhere',
    fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300,
    // Gate 6: a thumb target is at least 44×44 at phone width.
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  stepLink: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`, outlineOffset: tokens.spacingHorizontalXXS },
  },
  stepCurrent: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  number: {
    flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '1.5rem', height: '1.5rem', borderRadius: tokens.borderRadiusCircular,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200,
  },
  numberCurrent: {
    backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandBackground}`,
  },
  divider: { height: tokens.strokeWidthThin, backgroundColor: tokens.colorNeutralStroke2, marginBlock: tokens.spacingVerticalS },
});

export function OfferRecordFrame({ person, readiness, roles, currentStep, canEdit, children }: {
  readonly person: Person;
  /** Null while the checklist is still being derived; the badge simply is not drawn yet. */
  readonly readiness: ReadinessState | null;
  readonly roles: readonly string[];
  /** The step the rail marks as current — index into `candidateSteps`. */
  readonly currentStep: number;
  /** Gate 6: a Viewer is offered no way to change the record, not even a disabled one. */
  readonly canEdit: boolean;
  readonly children: ReactNode;
}) {
  const styles = useStyles();
  const steps = candidateSteps(person.id);
  const lifecycle = lifecyclePresenters[person.lifecycle];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.title}>
          <div className={styles.nameRow}>
            <h1 className={styles.name}>{personDisplayName(person.name)}</h1>
            {/* Staff added by hand are not tracked yet (ADR-016 §4): a derived "Setup required" would read as a fault. */}
            {!isComplianceTracked(person) ? <Caption1>Not tracked yet</Caption1> : readiness === null ? null : <ReadinessBadge state={readiness} />}
          </div>
          <div className={styles.roleRow}>
            <Badge appearance="outline" size="medium" shape="rounded">{lifecycle.label}</Badge>
            <Text>{roles.length > 0 ? roles.join(' · ') : 'No role set'}</Text>
          </div>
        </div>
        {canEdit ? <Button as="a" href={section(person.id, 'profile')}>Edit profile</Button> : null}
      </header>
      <div className={styles.body}>
        <nav aria-label="Candidate steps" className={styles.rail}>
          <Caption1 className={styles.railLabel}>Candidate steps</Caption1>
          {steps.map((step, index) => {
            const current = index === currentStep;
            const number = (
              <span aria-hidden="true" className={mergeClasses(styles.number, current && styles.numberCurrent)}>{index + 1}</span>
            );
            // mergeClasses, never a template literal: Griffel truncates a concatenated className
            // at the second sequence hash and silently drops everything after it.
            const className = mergeClasses(styles.step, step.href !== null && styles.stepLink, current && styles.stepCurrent);
            if (step.href === null || current) {
              return (
                <span key={step.label} className={className} aria-current={current ? 'step' : undefined}>
                  {number}<span>{step.label}</span>
                </span>
              );
            }
            return (
              <Link key={step.label} to={step.href} className={className}>
                {number}<span>{step.label}</span>
              </Link>
            );
          })}
          <div className={styles.divider} />
          {recordLinks(person.id).map((link) => (
            <Link key={link.label} to={link.href} className={mergeClasses(styles.step, styles.stepLink)}>{link.label}</Link>
          ))}
        </nav>
        {children}
      </div>
    </div>
  );
}
